/**
 * ItemsTable — sortable, filterable table for items within a category.
 * Used by: CategoriesTab (items sub-tab, returns sub-tab).
 *
 * Props:
 *   items       — array of item objects
 *   returnsOnly — show only return columns
 *   ticketList  — optional tickets to show per-item associations
 *   typeFilter  — active ticket-type filter key (or 'all')
 */
import React, { useState, useMemo, useEffect } from 'react';
import { fmt$, fmtN } from '../../../utils/formatUtils';
import { SortTh, useSortState, applySortStr, useColFilters, applyColFilters, FilterCell } from '../../../utils/tableUtils';
import { fmtTimeCst } from '../../../utils/cstUtils';
import { getTicketType, TICKET_TYPE_META } from '../squareDashboardUtils';
import TicketChips from './TicketChips';
import TableContextMenu, { useContextMenu } from './TableContextMenu';
import { downloadCSV } from '../../../utils/excelUtils';

// Column defs (stable references so hooks don't reinitialise on every render)
const FULL_COL_DEFS = [
  { key: 'name',          type: 'text'   },
  { key: 'qty',           type: 'min'    },
  { key: 'revenueCents',  type: 'minmax' },
  { key: 'discountCents', type: 'min'    },
  { key: 'returnQty',     type: 'min'    },
];
const RETURNS_COL_DEFS = [
  { key: 'name',      type: 'text' },
  { key: 'returnQty', type: 'min'  },
];

export default function ItemsTable({ items, returnsOnly, ticketList, typeFilter }) {
  const [expandedItem, setExpandedItem] = useState(null);
  const [sortKey, sortDir, onSort]      = useSortState('qty');

  const COL_DEFS = returnsOnly ? RETURNS_COL_DEFS : FULL_COL_DEFS;
  const [colFilters, setColFilter, clearFilters, hasActiveFilters] = useColFilters(COL_DEFS);

  // Reset expanded row when the type filter changes
  useEffect(() => { setExpandedItem(null); }, [typeFilter]);

  // Build item → ticket lookup
  const itemTktMap = useMemo(() => {
    if (!ticketList || !ticketList.length) return {};
    const map = {};
    ticketList.forEach(tkt => {
      (tkt.lines || []).forEach(line => {
        const k = line.name.toLowerCase();
        if (!map[k]) map[k] = [];
        if (!map[k].find(t => t.id === tkt.id))
          map[k].push({ id: tkt.id, shortId: tkt.shortId, ticketName: tkt.ticketName || '', time: tkt.time, status: tkt.status, discountCents: tkt.discountCents || 0 });
      });
    });
    return map;
  }, [ticketList]);

  const hasTickets = !!(ticketList && ticketList.length > 0);
  const colCount   = (returnsOnly ? 3 : 6) + (hasTickets ? 1 : 0);

  const sortedItems = useMemo(() => {
    const filtered = applyColFilters(items || [], colFilters, COL_DEFS);
    return applySortStr(filtered, sortKey, sortDir);
  }, [items, colFilters, COL_DEFS, sortKey, sortDir]);

  const tot = useMemo(
    () => sortedItems.reduce(
      (a, i) => ({
        qty:  a.qty  + i.qty,
        rev:  a.rev  + i.revenueCents,
        disc: a.disc + i.discountCents,
        rQty: a.rQty + i.returnQty,
        rRev: a.rRev + parseFloat(i.returnRevenue || 0),
      }),
      { qty: 0, rev: 0, disc: 0, rQty: 0, rRev: 0 }
    ),
    [sortedItems]
  );

  const [menuState, openMenu, closeMenu] = useContextMenu();
  const handleDownload = () => {
    const headers = returnsOnly
      ? ['Item', 'Returned Qty', 'Return Revenue']
      : ['Item', 'Qty', 'Revenue', 'Discount', 'Returned Qty', 'Return Revenue'];
    const rows = sortedItems.map(item => returnsOnly
      ? [item.name, item.returnQty, item.returnRevenue || 0]
      : [item.name, item.qty, (item.revenueCents / 100).toFixed(2), (item.discountCents / 100).toFixed(2), item.returnQty, item.returnRevenue || 0]
    );
    downloadCSV(headers, rows, `items-${new Date().toISOString().slice(0,10)}`);
  };

  if (!items || items.length === 0) return <p className="sq__empty">No items found.</p>;

  return (
    <div className="sq__table-wrap">
      {hasActiveFilters && (
        <div className="sq__filter-bar">
          <span className="sq__filter-bar-label">🔍 {sortedItems.length} of {items.length} items</span>
          <button className="sq__filter-clear" onClick={clearFilters}>&#x2715; Clear filters</button>
        </div>
      )}
      <TableContextMenu {...menuState} onClose={closeMenu} onDownload={handleDownload} />
      <table className="sq__table" onContextMenu={openMenu}>
        <thead>
          <tr>
            <SortTh col="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Item</SortTh>
            {!returnsOnly && (
              <>
                <SortTh col="qty"           sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Qty</SortTh>
                <SortTh col="revenueCents"  sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Revenue</SortTh>
                <SortTh col="discountCents" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Discount</SortTh>
              </>
            )}
            <SortTh col="returnQty" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Returned</SortTh>
            <th className="sq__num">Return $</th>
            {hasTickets && <th>Tickets</th>}
          </tr>
          <tr className="sq__filter-row">
            <FilterCell type="text" fval={colFilters.name} colKey="name" setFilter={setColFilter} />
            {!returnsOnly && (
              <>
                <FilterCell type="min"    fval={colFilters.qty}           colKey="qty"           setFilter={setColFilter} className="sq__num" />
                <FilterCell type="minmax" fval={colFilters.revenueCents}  colKey="revenueCents"  setFilter={setColFilter} className="sq__num" />
                <FilterCell type="min"    fval={colFilters.discountCents} colKey="discountCents" setFilter={setColFilter} className="sq__num" />
              </>
            )}
            <FilterCell type="min" fval={colFilters.returnQty} colKey="returnQty" setFilter={setColFilter} className="sq__num" />
            <td />
            {hasTickets && <td />}
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, i) => {
            const allTkts = hasTickets ? (itemTktMap[item.name.toLowerCase()] || []) : [];
            const tkts    = (typeFilter && typeFilter !== 'all')
              ? allTkts.filter(t => getTicketType(t) === typeFilter)
              : allTkts;
            const isExp   = expandedItem === item.name;
            return (
              <React.Fragment key={i}>
                <tr className={item.returnQty > 0 ? 'sq__row-return' : ''}>
                  <td className="sq__item-name">{item.name}</td>
                  {!returnsOnly && (
                    <>
                      <td className="sq__num">{fmtN(item.qty)}</td>
                      <td className="sq__num sq__revenue">{fmt$(item.revenue)}</td>
                      <td className="sq__num sq__discount">{item.discountCents > 0 ? fmt$(item.discount) : '\u2014'}</td>
                    </>
                  )}
                  <td className="sq__num">
                    {item.returnQty > 0 ? <span className="sq__return-badge">{item.returnQty}</span> : '\u2014'}
                  </td>
                  <td className="sq__num sq__return-rev">
                    {item.returnQty > 0 ? fmt$(item.returnRevenue) : '\u2014'}
                  </td>
                  {hasTickets && (
                    <td className="sq__itkt-cell">
                      <TicketChips
                        tickets={tkts}
                        expanded={isExp}
                        onExpand={() => setExpandedItem(isExp ? null : item.name)}
                      />
                    </td>
                  )}
                </tr>
                {hasTickets && isExp && tkts.length > 2 && (
                  <tr className="sq__itkt-expand-row">
                    <td colSpan={colCount}>
                      <div className="sq__itkt-poplist">
                        {tkts.map((t, j) => {
                          const ttype = getTicketType(t);
                          return (
                            <div key={j} className="sq__itkt-poprow">
                              <span className={`sq__tkt-typebadge sq__tkt-typebadge--${ttype}`}>
                                {TICKET_TYPE_META[ttype].label}
                              </span>
                              <span className="sq__itkt-popname">
                                {t.ticketName || <span className="sq__muted">\u2014</span>}
                              </span>
                              <span className="sq__itkt-poptime">{fmtTimeCst(t.time)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="sq__tfoot">
            <td>Total ({sortedItems.length})</td>
            {!returnsOnly && (
              <>
                <td className="sq__num">{fmtN(tot.qty)}</td>
                <td className="sq__num sq__revenue">{fmt$(tot.rev / 100)}</td>
                <td className="sq__num sq__discount">{tot.disc > 0 ? fmt$(tot.disc / 100) : '\u2014'}</td>
              </>
            )}
            <td className="sq__num">{tot.rQty > 0 ? fmtN(tot.rQty) : '\u2014'}</td>
            <td className="sq__num sq__return-rev">{tot.rRev > 0 ? fmt$(tot.rRev) : '\u2014'}</td>
            {hasTickets && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
